"use client";

/**
 * <ReactionText text="lol 😂"> — splits the input into emoji / non-emoji
 * segments and swaps every known emoji (glyph, alias, `:slug:` or `(slug)`)
 * with its custom <ReactionEmoji> while leaving unknown emojis (and plain
 * text) untouched.
 *
 * Use this wherever user-generated text might contain emojis: comments,
 * deal descriptions, blog excerpts, etc. The replacement is opt-out: if the
 * global reactions kill-switch is off, the component renders the raw string.
 */
import { Fragment, useMemo } from "react";
import { useReactions, useReactionsCatalog } from "@/lib/reactions/provider";
import {
  buildShortcodeMap,
  reactionGlyph,
  tokenizeReactionText,
} from "@/lib/reactions/shortcodes";
import { ReactionEmoji } from "./reaction-emoji";
import type {
  EmojiReactionMap,
  ReactionCatalogFilter,
  ReactionDefinition,
} from "@/lib/reactions/types";

export function ReactionText({
  text,
  className,
  filter,
}: {
  text: string;
  className?: string;
  filter?: ReactionCatalogFilter;
}) {
  const catalog = useReactionsCatalog();
  const filtered = useReactions(filter ?? {});
  const hasFilter = Boolean(filter && Object.keys(filter).length > 0);
  const enabled = hasFilter ? filtered.enabled : catalog.enabled;
  const byEmoji = useMemo(
    () => (hasFilter ? buildEmojiMap(filtered.list) : catalog.byEmoji),
    [catalog.byEmoji, filtered.list, hasFilter]
  );
  const byShortcode = useMemo(
    () => (hasFilter ? buildShortcodeMap(filtered.list) : catalog.byShortcode),
    [catalog.byShortcode, filtered.list, hasFilter]
  );

  const segments = useMemo(
    () => (enabled && text ? tokenizeReactionText(text, byEmoji, byShortcode) : null),
    [enabled, text, byEmoji, byShortcode]
  );

  if (!enabled || !segments) {
    return <span className={className}>{text}</span>;
  }

  return (
    <span className={className}>
      {segments.map((seg, i) => {
        if (!seg.reaction) return <Fragment key={i}>{seg.text}</Fragment>;
        return (
          <ReactionEmoji
            key={i}
            reaction={seg.reaction}
            fallback={reactionGlyph(seg.reaction) ?? seg.text}
            className="mx-[0.05em]"
          />
        );
      })}
    </span>
  );
}

export function ReactionRenderer({
  text,
  className,
  filter,
  preserveLineBreaks = false,
}: {
  text: string;
  className?: string;
  filter?: ReactionCatalogFilter;
  preserveLineBreaks?: boolean;
}) {
  if (!preserveLineBreaks) {
    return <ReactionText text={text} className={className} filter={filter} />;
  }
  const lines = text.split(/\r?\n/);
  return (
    <span className={className}>
      {lines.map((line, index) => (
        <Fragment key={index}>
          {index > 0 ? <br /> : null}
          <ReactionText text={line} filter={filter} />
        </Fragment>
      ))}
    </span>
  );
}

function buildEmojiMap(list: readonly ReactionDefinition[]): EmojiReactionMap {
  const map = new Map<string, ReactionDefinition>();
  for (const reaction of list) {
    if (reaction.emoji) map.set(reaction.emoji, reaction);
    for (const alias of reaction.aliases ?? []) {
      if (alias && !map.has(alias)) map.set(alias, reaction);
    }
  }
  return map;
}
